import { RefObject, useEffect, useState } from "react";
import throttle from "lodash.throttle";

/** Parametry usePopupFlip hooku */
export type UsePopupFlipParams = {
    /** Reference na spouštěč */
    triggerRef: RefObject<HTMLDivElement>;
    /** Reference na popup */
    popupRef?: RefObject<HTMLDivElement>;
    /** Pozice, kde se má okno prioritně zobrazit */
    positionPriority?: PopupPosition;
    /** Příznak, zda je otevírací oblast otevřená */
    opened: boolean;
    /** Výška contentu v popupu */
    height?: number;
    /** Šířka contentu v popupu */
    width?: number;
    /** Odsazení */
    offset?: number;
};

/**
 * Posun části popupu v závislosti na překlopení této části do dostupného místa
 */
export type OptionsContentOffset = {
    /** Horní posun */
    topOffset: number;
    /** Levý posun */
    leftOffset: number;
};

/** Směr otevření popupu */
export enum PopupPosition {
    rightBottom = "rightBottom",
    leftBottom = "leftBottom",
    rightTop = "rightTop",
    leftTop = "leftTop",
}

/** Výchozí délka throttle při změně směru popupu */
export const POPUP_FLIP_THROTTLE_MS = 15;

export const DEFAULT_POPUP_OFFSET = 0;

/**
 * Hook určený pro výpočet posunu popupu v závislosti na překlopení těla do dostupného místa.
 * Nespouští se při záměně obsahu části reprezentující spouštěč - při odlišné výšce této části se část repretentující okno ke spoustěči neposune.
 * Pozice okna se během výpočtu nachází mimo viditelnou oblast.
 * @param triggerRef Reference na spouštěč
 * @param opened Příznak, zda je otevírací oblast otevřená
 * @param popupRef Reference na popup
 * @param positionPriority Pozice, kde se má okno prioritně zobrazit
 * @param height Výška contentu v popupu.
 * @param width Šířka contentu v popupu.
 * @param offset Odsazení.
 */
export const usePopupFlip = ({ offset = DEFAULT_POPUP_OFFSET, ...params }: UsePopupFlipParams): OptionsContentOffset => {
    const [position, setPosition] = useState<OptionsContentOffset>({ topOffset: -window.innerHeight, leftOffset: -window.innerWidth });

    useEffect(() => {
        updatePosition();
        window.addEventListener("resize", throttledUpdatePosition, { passive: true, capture: true });
        window.addEventListener("scroll", throttledUpdatePosition, { passive: true, capture: true });

        return () => {
            window.removeEventListener("resize", throttledUpdatePosition);
            window.removeEventListener("scroll", throttledUpdatePosition);
        };
        // TODO [MB] - Opravit hook deps v rámci 30853.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.triggerRef, params.popupRef, params.opened]);

    const updatePosition = () => {
        if (!params.opened) {
            return;
        }

        const position = getPosition();
        if (position) {
            setPosition(position);
        }
    };

    const throttledUpdatePosition = throttle(updatePosition, POPUP_FLIP_THROTTLE_MS);

    /**
     * Podle dostupného místa kolem komponenty určí na kterou stranu se má popup otevírat.
     */
    const getPosition = (): OptionsContentOffset | undefined => {
        if (!(params.triggerRef?.current && (params.popupRef?.current || (params.height && params.width)))) {
            return undefined;
        }
        const triggerContentRect = params.triggerRef.current.getBoundingClientRect();
        const popupContentRect = params.popupRef?.current
            ? params.popupRef.current.getBoundingClientRect() : new DOMRect(0, 0, params.width, params.height);
        const contentPosition = getContentPosition(triggerContentRect, popupContentRect, params.positionPriority, offset);
        const offsets = getOffsets(triggerContentRect, popupContentRect, offset);
        return offsets[contentPosition];
    };

    return position;
};

export const getContentPosition = (triggerContentRect: DOMRect, popupContentRect: DOMRect, positionPriority?: PopupPosition, offset: number = DEFAULT_POPUP_OFFSET) => {
    const { rightSpace, leftSpace, topSpace, bottomSpace } = getSpaces(
        triggerContentRect,
        popupContentRect,
        window.innerWidth,
        window.innerHeight,
        offset,
    );

    const largestSpace = getLargestSpace(triggerContentRect, window.innerWidth, window.innerHeight);

    return getPopupPosition(rightSpace, bottomSpace, leftSpace, topSpace, positionPriority, largestSpace);
};

/**
 * Podle dostupného místa určí vhodnou pozici popupu
 * @param rightSpace Příznaky zda je dostupné místo vpravo
 * @param bottomSpace Příznaky zda je dostupné místo dole
 * @param leftSpace Příznaky zda je dostupné místo vlevo
 * @param topSpace Příznaky zda je dostupné místo nahoře
 * @param positionPriority Pozice, kde se má okno prioritně zobrazit
 * @param largestSpace Pozice, kde je k dispozici největší prostor
 */
export const getPopupPosition = (rightSpace: boolean, bottomSpace: boolean, leftSpace: boolean, topSpace: boolean, positionPriority?: PopupPosition, largestSpace?: PopupPosition) => {
    const spaces = {
        [ PopupPosition.leftBottom ]: (leftSpace && bottomSpace),
        [ PopupPosition.leftTop ]: (leftSpace && topSpace),
        [ PopupPosition.rightBottom ]: (rightSpace && bottomSpace),
        [ PopupPosition.rightTop ]: (rightSpace && topSpace),
    };

    // Pokud je to možné popup je vložen prioritní místo
    if (positionPriority && spaces[positionPriority]) {
        return positionPriority;
    }

    // Popup je vložen na první volné místo
    if (spaces[PopupPosition.rightBottom]) {
        return PopupPosition.rightBottom;
    }
    if (spaces[PopupPosition.leftBottom]) {
        return PopupPosition.leftBottom;
    }
    if (spaces[PopupPosition.rightTop]) {
        return PopupPosition.rightTop;
    }
    if (spaces[PopupPosition.leftTop]) {
        return PopupPosition.leftTop;
    }

    // Popup je vložen do největšího prostoru, pokud není, umístí se vpravo dolů
    return largestSpace ?? PopupPosition.rightBottom;
};

/** Zjistí, zda je v jednotlivých směrech dostatek místa. */
export const getSpaces = (
    triggerContentRect: DOMRect,
    popupContentRect: DOMRect,
    windowWidth: number,
    windowHeight: number,
    offset: number = DEFAULT_POPUP_OFFSET,
) => ({
    rightSpace: popupContentRect.width <= windowWidth - triggerContentRect.left,
    leftSpace: popupContentRect.width <= triggerContentRect.right,
    topSpace: popupContentRect.height + offset <= triggerContentRect.top,
    bottomSpace: popupContentRect.height <= windowHeight - triggerContentRect.bottom - offset,
});

/** Zjistí největší prostor */
export const getLargestSpace = (
    triggerContentRect: DOMRect,
    windowWidth: number,
    windowHeight: number,
): PopupPosition => {
    if (triggerContentRect.top > windowHeight - triggerContentRect.bottom) {
        return windowWidth - triggerContentRect.right > triggerContentRect.left
            ? PopupPosition.rightTop : PopupPosition.leftTop;
    } else {
        return windowWidth - triggerContentRect.right > triggerContentRect.left
            ? PopupPosition.rightBottom : PopupPosition.leftBottom;
    }
};

/**
 * Získá posuny pro jednotlivé pozice pro popup.
 * Pokud je popup součástí iframe, výsledná hodnota se upraví podle polohy scrollbaru který scrolluje dokument.
 */
export const getOffsets = (triggerContentRect: DOMRect, popupContentRect: DOMRect, offset: number = DEFAULT_POPUP_OFFSET) => {
    const offsets = {
        [PopupPosition.leftBottom]: {
            topOffset: triggerContentRect.bottom + offset,
            leftOffset: -popupContentRect.width + triggerContentRect.right,
        },
        [PopupPosition.rightBottom]: {
            topOffset: triggerContentRect.bottom + offset,
            leftOffset: triggerContentRect.left,
        },
        [PopupPosition.leftTop]: {
            topOffset: triggerContentRect.top - popupContentRect.height - offset,
            leftOffset: -popupContentRect.width + triggerContentRect.right,
        },
        [PopupPosition.rightTop]: {
            topOffset: triggerContentRect.top - popupContentRect.height - offset,
            leftOffset: triggerContentRect.left,
        },
    };
    const scrollingElement = document.scrollingElement;
    if (window !== window.top && scrollingElement) {
        const { scrollLeft, scrollTop } = scrollingElement;
        Object.keys(PopupPosition).forEach((space) => {
            const offset = offsets[space as PopupPosition];
            offset.topOffset += scrollTop;
            offset.leftOffset += scrollLeft;
        });
    }
    return offsets;
};
